import { FEATURES } from '@genealogy-app/shared';

// ─── Was der Rundgang erzählt ────────────────────────────────────────────────
//
// Getrennt von der Darstellung, weil das hier der Teil ist, der sich ändert:
// eine neue Seite, ein anderer Einstieg, ein Satz, der sich als unklar
// herausgestellt hat.
//
// Zwei Regeln für die Texte:
//
//   1. Sie sagen, WOZU etwas da ist, nicht wo es steht. „Hier klicken" erklärt
//      nichts – der Pfeil zeigt ohnehin schon hin.
//   2. Sie versprechen nichts, was der Tarif nicht hergibt. Deshalb sind die
//      Funktionsschritte nicht fest verdrahtet, sondern kommen aus dem, was
//      der Server als freigeschaltet meldet.

export interface Schritt {
  /**
   * Wert von `data-rundgang` am hervorzuhebenden Element. Fehlt = mittig.
   *
   * Mehrere Werte sind Ausweichmöglichkeiten in dieser Reihenfolge: am Handy
   * liegen Navigation, Person-Knopf und Baumwechsler im Hamburger-Menü, also
   * zeigt der Rundgang dort auf das Menü statt auf gar nichts.
   */
  anker?: string | string[];
  titel: string;
  text: string;
}

/**
 * Der Einstieg – für Konten, die zum ersten Mal da sind.
 *
 * Bewusst kurz. Sieben Karten sind die Grenze dessen, was jemand liest, bevor
 * er anfängt, auf „Weiter" zu hämmern, um endlich loszulegen.
 */
export const START_SCHRITTE: Schritt[] = [
  {
    titel: 'Willkommen',
    // Ohne Zahl: die erste Fassung sagte „fünf Stationen" und zeigte sechs.
    // Eine Angabe, die bei jedem neuen Schritt nachgezogen werden muss, wird
    // irgendwann nicht nachgezogen.
    text:
      'Ein kurzer Rundgang durch die Anwendung, keine Minute. Sie können ihn ' +
      'jederzeit überspringen und später im Benutzermenü wieder starten.',
  },
  {
    anker: ['navigation', 'menue'],
    titel: 'Was es außer dem Baum noch gibt',
    text:
      'Archiv für Fotos und Urkunden, Analysen zu Namen, Orten und Lebensdauer, ' +
      'Forschung für offene Fragen und fehlende Belege.',
  },
  {
    anker: ['person', 'menue'],
    titel: 'Personen eintragen',
    text:
      'Der Anfang ist eine Person – meist man selbst. Eltern, Kinder und ' +
      'Geschwister kommen danach dazu, direkt am Knoten im Baum.',
  },
  {
    anker: 'suche',
    titel: 'Suchen statt scrollen',
    text:
      'Findet Personen, Orte und Seiten. Geht auch ohne Maus: Strg + K, auf dem ' +
      'Mac ⌘ + K.',
  },
  {
    anker: ['baumwechsel', 'menue'],
    titel: 'Mehrere Stammbäume',
    text:
      'Jede Familie ein eigener Baum – väterlich, mütterlich, angeheiratet. ' +
      'Sie bleiben getrennt, und wer mitarbeiten darf, entscheiden Sie je Baum.',
  },
  {
    anker: 'menue',
    titel: 'Konto, Mitglieder, Chronik',
    text:
      'Hier liegen Ihre Einstellungen, die Mitglieder Ihres Stammbaums und die ' +
      'Chronik als PDF. Und der Knopf, mit dem Sie diesen Rundgang neu starten.',
  },
];

/** Ein Satz je Funktion – kommt aus dem Katalog, damit nichts doppelt gepflegt wird. */
export function funktionsSchritte(schluessel: string[]): Schritt[] {
  const katalog = new Map(FEATURES.map((f) => [f.key, f]));
  return schluessel
    .map((k) => katalog.get(k))
    .filter((f): f is NonNullable<typeof f> => Boolean(f))
    .map((f) => ({
      anker: ['navigation', 'menue'],
      titel: f.label,
      text: f.description,
    }));
}
